import { useQuery } from '@tanstack/react-query';
import { TRANSITIONS, type TicketAction } from '@lixionary-jira/domain';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, useAction } from '../../api/hooks';
import type { TicketDetail } from '../../api/ticket-types';
import { Alert, PageLoading } from '../../ui/feedback';
import { Field, Segmented, Textarea } from '../../ui/forms';
import { Markdown } from '../../ui/Markdown';
import { ConfirmDialog } from '../../ui/Modal';
import { KeyValue } from '../../ui/misc';

export type DialogAction = TicketAction | 'start_manual' | 'rework';

const NEEDS_REASON: DialogAction[] = ['reject', 'reconsider', 'reopen', 'rework', 'release_claim'];

/**
 * Guarded status change (WF-03): every move is a named domain action with its own review, never a raw
 * column update. Used by the board (including its keyboard alternative) and ticket detail.
 */
export function TicketActionDialog({
  projectKey,
  number,
  action,
  onClose,
  onDone,
}: {
  projectKey: string;
  number: number;
  action: DialogAction;
  onClose: () => void;
  onDone?: () => void;
}) {
  const q = useQuery({
    queryKey: ['ticket', projectKey, number],
    queryFn: () => api.get<TicketDetail>(`/projects/${projectKey}/tickets/${number}`),
  });
  const [reason, setReason] = useState('');
  const [target, setTarget] = useState<'todo' | 'in_progress'>(
    action === 'rework_in_progress' ? 'in_progress' : 'todo',
  );
  const t = q.data;
  const base = `/projects/${projectKey}/tickets/${number}`;
  const run = useAction(
    async () => {
      const v = t!.version;
      switch (action) {
        case 'accept':
          return api.post(`${base}/accept`, { expectedVersion: v, scopeRevision: t!.scopeRevision });
        case 'reject':
          return api.post(`${base}/reject`, { expectedVersion: v, reason });
        case 'reconsider':
          return api.post(`${base}/reconsider`, { expectedVersion: v, reason });
        case 'reopen':
          return api.post(`${base}/reopen`, { expectedVersion: v, reason });
        case 'rework':
        case 'rework_todo':
        case 'rework_in_progress':
          return api.post(`${base}/rework`, { expectedVersion: v, reason, target });
        case 'claim':
        case 'start_manual':
          return api.post(`${base}/implementation/start`, { expectedVersion: v });
        case 'release_claim':
          return api.post(`${base}/implementation/release`, { expectedVersion: v, reason });
        default:
          throw new Error('This action is performed from the implementation page.');
      }
    },
    {
      success: 'Ticket updated',
      invalidate: [
        ['ticket', projectKey, number],
        ['board', projectKey],
        ['dashboard', projectKey],
        ['activity', projectKey],
        ['me'],
      ],
      onSuccess: () => {
        onDone?.();
        onClose();
      },
    },
  );

  if (!t)
    return (
      <ConfirmDialog
        title="Loading…"
        confirmLabel="Confirm"
        onConfirm={() => undefined}
        onClose={onClose}
        disabled
      >
        {q.error ? <Alert tone="error">This ticket could not load.</Alert> : <PageLoading />}
      </ConfirmDialog>
    );

  const label =
    action === 'start_manual' || action === 'claim'
      ? 'Take manual ownership'
      : action === 'rework'
        ? 'Rework'
        : TRANSITIONS[action as TicketAction].label;
  const needsReason = NEEDS_REASON.includes(action) || action.startsWith('rework');
  const danger = action === 'reject';
  const openUnresolved = t.descendants.filter((d) => d.status !== 'closed');

  if (action === 'mark_ready') {
    return (
      <ConfirmDialog
        title={`${label}: ${t.key}`}
        confirmLabel="Open implementation"
        onConfirm={onClose}
        onClose={onClose}
      >
        <p>Readiness requires complete implementation evidence. Review it on the implementation page.</p>
        {t.owner && (
          <Link to={`/projects/${projectKey}/tickets/${number}/implementations/${t.owner.displayId}`}>
            Open {t.owner.displayId}
          </Link>
        )}
      </ConfirmDialog>
    );
  }

  return (
    <ConfirmDialog
      title={`${label}: ${t.key}`}
      confirmLabel={action === 'accept' ? 'Accept this scope' : danger ? 'Yes, reject' : label}
      variant={danger ? 'danger' : 'primary'}
      onConfirm={() => run.mutate(undefined)}
      onClose={onClose}
      pending={run.isPending}
      error={run.error}
      disabled={needsReason && !reason.trim()}
      wide={action === 'accept'}
    >
      {action === 'accept' && (
        <>
          <p className="muted">
            You are accepting scope revision {t.scopeRevision}. Acceptance authorizes implementation of
            exactly this scope; it does not authorize deployment.
          </p>
          <div className="well stack sm">
            <span className="strong">{t.title}</span>
            <Markdown>{t.description}</Markdown>
            {t.stepsOrOutcome && (
              <>
                <span className="small strong">
                  {t.type === 'issue' ? 'Reproduction steps' : 'Intended outcome'}
                </span>
                <Markdown>{t.stepsOrOutcome}</Markdown>
              </>
            )}
            <span className="small strong">Acceptance criteria</span>
            {t.acceptanceCriteria.length ? (
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                {t.acceptanceCriteria.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ol>
            ) : (
              <Alert tone="error">Add acceptance criteria before accepting.</Alert>
            )}
          </div>
          <KeyValue
            items={[
              ['Parent', t.parent ? `${t.parent.key} · ${t.parent.title}` : 'None'],
              ['Children', t.children.length ? t.children.map((c) => c.key).join(', ') : 'None'],
              [
                'Prerequisites',
                t.prerequisites.length
                  ? t.prerequisites.map((p) => `${p.key} (${p.status.replace('_', ' ')})`).join(', ')
                  : 'None',
              ],
              [
                'Context',
                <Link key="c" to={`/projects/${projectKey}/context/overview`}>
                  Review project context
                </Link>,
              ],
            ]}
          />
          {t.blocked && (
            <Alert tone="warning">
              This ticket is blocked. Acceptance is allowed, but work cannot start until blockers are resolved
              or overridden.
            </Alert>
          )}
        </>
      )}
      {action === 'reject' && (
        <>
          <p>
            The ticket closes as Rejected. Active claims end, pending acceptance requests are cancelled, and
            non-executing releases that include it are cancelled.
          </p>
          {openUnresolved.length > 0 && (
            <Alert tone="info">Children stay open; each needs its own decision.</Alert>
          )}
          {t.parent && (
            <Alert tone="info">The parent {t.parent.key} will need an admin parent-scope decision.</Alert>
          )}
        </>
      )}
      {action === 'reconsider' && (
        <>
          <p>
            The ticket returns to Pending. This revokes acceptance and execution authority and invalidates
            readiness and release approvals. Prior work and evidence stay in history.
          </p>
          {t.owner && (
            <Alert tone="warning">
              The current claim by {t.owner.name} ends. The application cannot stop an external process; tell
              the owner.
            </Alert>
          )}
          {t.parent?.status === 'pending_release' && (
            <Alert tone="warning">Ready parent {t.parent.key} will be sent back for rework.</Alert>
          )}
          {t.releases.some((r) => r.reserved) && (
            <Alert tone="warning">Release approvals that include this ticket become stale.</Alert>
          )}
        </>
      )}
      {(action === 'rework' || action === 'rework_todo' || action === 'rework_in_progress') && (
        <>
          <p>
            Readiness and release approval for this ticket are invalidated. Accepted scope stays the same.
          </p>
          <Segmented
            label="Rework target"
            value={target}
            onChange={setTarget}
            options={[
              { value: 'todo', label: 'Todo (AI rework)' },
              { value: 'in_progress', label: 'In progress (my ownership)' },
            ]}
          />
        </>
      )}
      {action === 'reopen' && (
        <p>
          The ticket returns to Pending, clears its resolution, unarchives, and needs fresh acceptance.
          Delivery history is kept.
        </p>
      )}
      {(action === 'start_manual' || action === 'claim') && (
        <p>
          You take ownership of the implementation. The ticket moves to In progress and is no longer
          AI-claimable. You'll record evidence on the implementation page.
        </p>
      )}
      {action === 'release_claim' && (
        <p>
          The ticket returns to Todo with its acceptance. Work references are preserved for the next owner.
        </p>
      )}
      {needsReason && (
        <Field label="Reason" hint="Recorded in the ticket history and audit.">
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-autofocus
            />
          )}
        </Field>
      )}
    </ConfirmDialog>
  );
}
